import { createTheme } from '@mui/material/styles';
import { lightModeShades } from '../colors/lightModeShades';
import '../themeAugmentations';
import { UNIVERSAL_THEME_ELEMENTS } from './universalThemeElements';

const universalMuiButton = UNIVERSAL_THEME_ELEMENTS?.components?.MuiButton;
const universalStyleOverrides = universalMuiButton?.styleOverrides || {};
const universalRootStyles = universalMuiButton?.styleOverrides?.root || {};
const universalVariants = universalMuiButton?.variants || [];

export const LIGHT_MODE = createTheme({
  ...(UNIVERSAL_THEME_ELEMENTS || {}),
  palette: {
    text: {
      primary: lightModeShades.grey[600],
      secondary: lightModeShades.blue[600],
    },
    strokePrimary: lightModeShades.navy[75],
    strokeSecondary: lightModeShades.navy[500],
    primary: {
      main: lightModeShades.navy[900],
      contrastText: lightModeShades.grey[100],
    },
    secondary: {
      main: lightModeShades.navy[500],
      contrastText: lightModeShades.grey[100],
    },
    tertiary: {
      main: lightModeShades.peach[300],
      contrastText: lightModeShades.navy[900],
    },
    neutral: {
      main: lightModeShades.cream[50],
      contrastText: lightModeShades.navy[900],
    },
    neutralLight: {
      main: lightModeShades.cream[25],
      contrastText: lightModeShades.navy[900],
    },
    error: {
      main: lightModeShades.red[400],
      contrastText: lightModeShades.navy[900],
    },
    success: {
      main: lightModeShades.green[400],
      contrastText: lightModeShades.navy[900],
    },
    warning: {
      main: lightModeShades.yellow[400],
      contrastText: lightModeShades.navy[900],
    },
    errorContainer: {
      main: lightModeShades.red[200],
      contrastText: lightModeShades.navy[500],
    },
    successContainer: {
      main: lightModeShades.green[100],
      contrastText: lightModeShades.navy[500],
    },
    surfaceContainer: {
      main: lightModeShades.grey[225],
      contrastText: lightModeShades.grey[900],
    },
    warningContainer: {
      main: lightModeShades.yellow[200],
      contrastText: lightModeShades.navy[500],
    },
    primaryAccent: {
      main: lightModeShades.peach[300],
      contrastText: lightModeShades.navy[900],
    },
    neutralAccent: {
      main: lightModeShades.navy[50],
      contrastText: lightModeShades.navy[500],
    },
    surfaceAccents: {
      blue: lightModeShades.accent?.blue,
      coral: lightModeShades.accent?.coral,
      neutral: lightModeShades.navy[50],
      neutralDark: lightModeShades.navy[200],
      purple: lightModeShades.accent?.purple,
      teal: lightModeShades.teal[50],
    },
    surfaces: {
      neutralLighter: lightModeShades.cream[10],
    },
    blueShades: lightModeShades.blue,
    creamShades: lightModeShades.cream,
    greenShades: lightModeShades.green,
    greyShades: lightModeShades.grey,
    navyShades: lightModeShades.navy,
    peachShades: lightModeShades.peach,
    redShades: lightModeShades.red,
    secondaryShades: lightModeShades.secondary,
    tealShades: lightModeShades.teal,
    yellowShades: lightModeShades.yellow,
  },
  components: {
    MuiCssBaseline: {
      ...(UNIVERSAL_THEME_ELEMENTS?.components?.MuiCssBaseline || {}),
    },
    MuiAvatar: {
      ...(UNIVERSAL_THEME_ELEMENTS?.components?.MuiAvatar || {}),
      styleOverrides: {
        root: ({ theme }) => ({
          ...(UNIVERSAL_THEME_ELEMENTS?.components?.MuiAvatar?.styleOverrides?.root?.(
            { theme }
          ) || {}),
          '&.blue': {
            backgroundColor: theme.palette.surfaceAccents?.blue,
          },
          '&.coral': {
            backgroundColor: theme.palette.surfaceAccents?.coral,
          },
          '&.neutral': {
            backgroundColor: theme.palette.surfaceAccents?.neutral,
          },
          '&.neutralDark': {
            backgroundColor: theme.palette.surfaceAccents?.neutralDark,
          },
          '&.purple': {
            backgroundColor: theme.palette.surfaceAccents?.purple,
          },
          '&.teal': {
            backgroundColor: theme.palette.surfaceAccents?.teal,
          },
          '&.errorLight': {
            backgroundColor: theme.palette.redShades[200],
          },
          '&.warningLight': {
            backgroundColor: theme.palette.yellowShades[200],
          },
        }),
      },
    },
    MuiBadge: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiBadge-badge': {
            color: theme.palette.primary?.contrastText,
          },
        }),
      },
      variants: [
        {
          props: { color: 'error' },
          style: ({ theme }) => ({
            '& .MuiBadge-badge': {
              backgroundColor: theme.palette.error?.main,
            },
          }),
        },
        {
          props: { color: 'neutral' },
          style: ({ theme }) => ({
            '& .MuiBadge-badge': {
              backgroundColor: theme.palette.secondary?.main,
            },
          }),
        },
        {
          props: { color: 'success' },
          style: ({ theme }) => ({
            '& .MuiBadge-badge': {
              backgroundColor: theme.palette.success?.main,
            },
          }),
        },
        {
          props: { color: 'warning' },
          style: ({ theme }) => ({
            '& .MuiBadge-badge': {
              backgroundColor: theme.palette.warning?.main,
            },
          }),
        },
      ],
    },
    MuiButton: {
      variants: [
        ...universalVariants,
        {
          props: { variant: 'contained' },
          style: ({ theme }) => ({
            color: theme.palette.primary?.contrastText,
            backgroundColor: theme.palette.secondary?.main,

            '& svg': {
              fill: theme.palette.greyShades?.[100],
            },

            '&:hover': {
              backgroundColor: theme.palette.blueShades?.[700],
            },
            '&:focus': {
              backgroundColor: theme.palette.blueShades?.[700],
            },
            '&:active': {
              backgroundColor: theme.palette.blueShades?.[700],
            },
          }),
        },
        {
          props: { variant: 'tonal' },
          style: ({ theme }) => ({
            color: theme.palette.primary?.main,
            backgroundColor: theme.palette.blueShades?.[200],

            '&:hover': {
              backgroundColor: theme.palette.greyShades?.[225],
            },
            '&:focus': {
              backgroundColor: theme.palette.greyShades?.[225],
            },
            '&:active': {
              backgroundColor: theme.palette.greyShades?.[250],
            },
          }),
        },
        {
          props: { variant: 'destructive' },
          style: ({ theme }) => ({
            color: theme.palette.greyShades?.[100],
            backgroundColor: theme.palette.error?.main,

            '& svg': {
              fill: theme.palette.greyShades?.[100],
            },

            '&:hover': {
              backgroundColor: theme.palette.error?.main,
            },
            '&:focus': {
              backgroundColor: theme.palette.error?.main,
            },
            '&:active': {
              backgroundColor: theme.palette.error?.main,
            },
          }),
        },
        {
          props: { variant: 'outlined' },
          style: ({ theme }) => ({
            color: theme.palette.navyShades?.[500],
            backgroundColor: 'transparent',
            border: `1px solid ${theme.palette.strokeSecondary}`,
            '&:hover': {
              backgroundColor: theme.palette.greyShades?.[225],
            },
            '&:focus': {
              backgroundColor: theme.palette.greyShades?.[225],
            },
            '&:active': {
              backgroundColor: theme.palette.greyShades?.[250],
            },
          }),
        },
        {
          props: { variant: 'text' },
          style: ({ theme }) => ({
            color: theme.palette.text.secondary,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: theme.palette.greyShades?.[225],
            },
            '&:focus': {
              backgroundColor: theme.palette.greyShades?.[225],
            },
            '&:active': {
              backgroundColor: theme.palette.greyShades?.[250],
            },
          }),
        },
        /*
         * The text variant pins a dark ink color, so `color="inherit"` has no
         * effect without this entry. Text buttons on dark surfaces need it to
         * take their color and hover wash from the surface they sit on.
         */
        {
          props: { variant: 'text', color: 'inherit' },
          style: () => ({
            color: 'inherit',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.16)',
            },
            '&:focus': {
              backgroundColor: 'rgba(255, 255, 255, 0.16)',
            },
            '&:active': {
              backgroundColor: 'rgba(255, 255, 255, 0.24)',
            },
          }),
        },
      ],
      styleOverrides: {
        ...universalStyleOverrides,

        root: ({ theme }) => {
          const baseRoot =
            typeof universalRootStyles === 'function'
              ? universalRootStyles({ theme })
              : universalRootStyles;

          return {
            ...baseRoot,
          };
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: '100%',
          boxShadow: 'none',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.shape.borderRadius,
          boxSizing: 'border-box',

          '& .MuiCardContent-root:last-child': {
            paddingBottom: '16px',
          },
        }),
      },
      defaultProps: {
        elevation: 0,
      },
    },
    MuiChip: {
      ...(UNIVERSAL_THEME_ELEMENTS?.components?.MuiChip || {}),
      variants: [
        {
          props: {
            variant: 'outlined',
            color: 'error',
          },
          style: ({ theme }) => ({
            backgroundColor: 'transparent',
            border: `2px solid ${theme.palette.errorContainer?.main}`,
          }),
        },
        {
          props: {
            variant: 'outlined',
            color: 'neutral',
          },
          style: ({ theme }) => ({
            color: theme.palette.blueShades?.[600],
            backgroundColor: theme.palette.navyShades?.[200],
            border: `1px solid ${theme.palette.blueShades?.[600]}`,
            '&:hover': {
              color: theme.palette.blueShades?.[600],
              backgroundColor: theme.palette.greyShades?.[225],
            },
            '&:focused': {
              color: theme.palette.greyShades?.[100],
              backgroundColor: theme.palette.blueShades?.[600],
            },
            '&:disabled': {
              color: theme.palette.greyShades?.[300],
              backgroundColor: theme.palette.greyShades?.[250],
            },

            '&.saturated': {
              border: `2px solid ${theme.palette.neutral?.main}`,
            },
          }),
        },
        {
          props: {
            variant: 'outlined',
            color: 'success',
          },
          style: ({ theme }) => ({
            backgroundColor: 'transparent',
            border: `2px solid ${theme.palette.successContainer?.main}`,

            '&.saturated': {
              color: theme.palette.text.primary,
              border: `2px solid ${theme.palette.success?.main}`,
            },
          }),
        },
        {
          props: {
            variant: 'outlined',
            color: 'warning',
          },
          style: ({ theme }) => ({
            backgroundColor: 'transparent',
            border: `2px solid ${theme.palette.warningContainer?.main}`,

            '&.saturated': {
              border: `2px solid ${theme.palette.warning?.main}`,
            },
          }),
        },
        {
          props: {
            color: 'error',
            variant: 'filled',
          },
          style: ({ theme }) => ({
            '&:not(.tag)': {
              color: theme.palette.primary?.contrastText,
            },

            '&.tag': {
              backgroundColor: theme.palette.errorContainer?.main,

              '&.saturated': {
                color: theme.palette.primary?.contrastText,
                backgroundColor: theme.palette.error?.main,
              },
            },
          }),
        },
        {
          props: {
            color: 'neutral',
            variant: 'filled',
          },
          style: ({ theme }) => ({
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.blueShades?.[200],
            '&:hover': {
              backgroundColor: theme.palette.greyShades?.[225],
            },
            '&:focus': {
              backgroundColor: theme.palette.greyShades?.[225],
            },
            '&.selected': {
              color: theme.palette.primary?.contrastText,
              backgroundColor: theme.palette.blueShades?.[500],
            },
            '&:disabled': {
              backgroundColor: theme.palette.secondary?.main,
            },
          }),
        },
        {
          props: {
            color: 'success',
            variant: 'filled',
          },
          style: ({ theme }) => ({
            '&:not(.tag)': {
              color: theme.palette.primary?.contrastText,
            },

            '&.tag': {
              backgroundColor: theme.palette.successContainer?.main,

              '&.saturated': {
                color: theme.palette.greyShades?.[100],
                backgroundColor: theme.palette.success?.main,
              },
            },
          }),
        },
        {
          props: {
            color: 'warning',
            variant: 'filled',
          },
          style: ({ theme }) => ({
            '&:not(.tag)': {
              color: theme.palette.primary?.contrastText,
            },

            '&.tag': {
              backgroundColor: theme.palette.warningContainer?.main,

              '&.saturated': {
                backgroundColor: theme.palette.warning?.main,
              },
            },
          }),
        },
        {
          props: {
            variant: 'outlined',
            color: 'error',
          },
          style: ({ theme }) => ({
            '&.tag': {
              backgroundColor: 'transparent',
              border: `1px solid ${theme.palette.errorContainer?.main}`,

              '&.saturated': {
                color: theme.palette.primary?.contrastText,
                border: `2px solid ${theme.palette.error?.main}`,
              },
            },
          }),
        },
        {
          props: {
            variant: 'outlined',
            color: 'neutral',
          },
          style: ({ theme }) => ({
            '&.tag': {
              backgroundColor: 'transparent',
              border: `1px solid ${theme.palette.neutralAccent?.main}`,

              '&.saturated': {
                color: theme.palette.primary?.main,
                border: `2px solid ${theme.palette.strokeSecondary}`,
              },
            },
          }),
        },
        {
          props: {
            variant: 'outlined',
            color: 'success',
          },
          style: ({ theme }) => ({
            '&.tag': {
              backgroundColor: 'transparent',
              border: `1px solid ${theme.palette.successContainer?.main}`,

              '&.saturated': {
                color: theme.palette.secondary?.main,
                border: `2px solid ${theme.palette.success?.main}`,
              },
            },
          }),
        },
        {
          props: {
            variant: 'outlined',
            color: 'warning',
          },
          style: ({ theme }) => ({
            '&.tag': {
              backgroundColor: 'transparent',
              border: `1px solid ${theme.palette.warningContainer?.main}`,

              '&.saturated': {
                border: `2px solid ${theme.palette.warning?.main}`,
              },
            },
          }),
        },
      ],
    },
    MuiInputBase: {
      ...(UNIVERSAL_THEME_ELEMENTS?.components?.MuiInputBase || {}),
    },
    MuiInputLabel: {
      ...(UNIVERSAL_THEME_ELEMENTS?.components?.MuiInputLabel || {}),
    },
    MuiSpeedDial: {
      ...(UNIVERSAL_THEME_ELEMENTS?.components?.MuiSpeedDial || {}),
    },
    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme }) => ({
          backgroundColor: theme.palette.secondary?.main,
        }),
      },
    },
    MuiToggleButtonGroup: {
      ...(UNIVERSAL_THEME_ELEMENTS?.components?.MuiToggleButtonGroup || {}),
      styleOverrides: {
        root: ({ theme }) => {
          const baseRootStyles =
            UNIVERSAL_THEME_ELEMENTS?.components?.MuiToggleButtonGroup?.styleOverrides?.root?.(
              { theme }
            ) || {};
          return {
            ...baseRootStyles,
            backgroundColor: theme.palette.blueShades?.[200],
            '&.toggle-switch': {
              ...(baseRootStyles['&.toggle-switch'] || {}),
              backgroundColor: theme.palette.blueShades?.[200],
            },
          };
        },
        grouped: {
          ...(UNIVERSAL_THEME_ELEMENTS?.components?.MuiToggleButtonGroup
            ?.styleOverrides?.grouped || {}),
        },
      },
    },
    MuiToggleButton: {
      ...(UNIVERSAL_THEME_ELEMENTS?.components?.MuiToggleButton || {}),
    },
    MuiTypography: {
      ...(UNIVERSAL_THEME_ELEMENTS?.components?.MuiTypography || {}),
    },
  },
});
